import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getImageUrlHelper, truncateText } from "../../helper/helper";
import {
    createAds,
    deleteAds,
    getAds,
    updateAds,
} from "../../redux/ads/adsSlice";
import useDebounce from "../../hook/useDebounce";
import { processFileUpload, FILE_TYPES } from "../../utils/fileUploadHelper";
import { useLocationFilter } from "../../context/LocationContext";
import LocationSelectComponent from "../../components/common/LocationSelectComponent";
import PaginationHelper from "../../helper/PaginationHelper";

const initialState = {
    location: null,
    title: "",
    description: "",
    link: "",
    file: null,
};
const DESC_MAX = 255;

const AdsLayer = () => {
    const dispatch = useDispatch();
    const { selectedLocation } = useLocationFilter();
    const { ads = [], total = 0 } = useSelector((state) => state.ads);

    const [search, setSearch] = useState("");
    const [entries, setEntries] = useState(50);
    const [currentPage, setCurrentPage] = useState(1);
    const debouncedSearch = useDebounce(search, 1000);

    const [formData, setFormData] = useState(initialState);
    const [isEdit, setIsEdit] = useState(false);
    const [editId, setEditId] = useState(null);
    const [deleteId, setDeleteId] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    useEffect(() => {
        dispatch(
            getAds({
                page: currentPage,
                limit: entries,
                search: debouncedSearch,
                locationId: selectedLocation?.value || "",
            })
        );
    }, [dispatch, currentPage, entries, debouncedSearch, selectedLocation]);

    const totalPages = Math.ceil(total / entries);

    const openCreate = () => {
        setFormData(initialState);
        setImagePreview(null);
        setIsEdit(false);
    };

    const openEdit = (a) => {
        setFormData({
            location: {
                value: a.locationId,
                label: a.locationName,
            },
            title: a.title || "",
            description: a.description || "",
            link: a.link || "",
            file: null,
        });

        setImagePreview(getImageUrlHelper(a.path));

        setEditId(a.id);
        setIsEdit(true);
    };

    const handleSubmit = () => {
        const { title, description, link, file } = formData;
        if (!formData.location) {
            toast.error("Location is required");
            return;
        }


        if (!title || !title.trim()) {
            toast.error("Title is required");
            return;
        }

        if (!link || !link.trim()) {
            toast.error("Link is required");
            return;
        }

        if (!/^https?:\/\/.+\..+/.test(link)) {
            toast.error("Enter valid URL (http/https)");
            return;
        }

        if (description && description.length > DESC_MAX) {
            toast.error(`Description max ${DESC_MAX} characters`);
            return;
        }

        if (!file && !isEdit) {
            toast.error("Image is required");
            return;
        }

        const fd = new FormData();
        fd.append("locationId", formData.location.value);
        fd.append("title", title);
        fd.append("description", description);
        fd.append("link", link);
        if (file) fd.append("file", file);

        const action = isEdit
            ? updateAds({ id: editId, data: fd })
            : createAds(fd);

        dispatch(action)
            .unwrap()
            .then((res) => {
                toast.success(
                    res?.message ||
                    res?.data?.message ||
                    "Operation successful"
                );

                dispatch(getAds({
                    page: currentPage,
                    limit: entries,
                    search: debouncedSearch,
                    locationId: selectedLocation?.value || "",
                }));

                document.querySelector("#adsModal .btn-close")?.click();
            })
            .catch((err) => {
                toast.error(
                    err?.response?.data?.message ||
                    err?.message ||
                    "Something went wrong"
                );
            });
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const processedFile = await processFileUpload(file, {
            allowedTypes: FILE_TYPES.IMAGE,
        });

        if (!processedFile) return;

        setFormData({ ...formData, file: processedFile });
        setImagePreview(URL.createObjectURL(processedFile));
    };

    return (
        <>
            <div className="card h-100 p-0 radius-12">

                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">

                    <div className="d-flex align-items-center flex-wrap gap-3">

                        <span className="text-md fw-medium text-secondary-light">
                            Show
                        </span>

                        <select
                            className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
                            value={entries}
                            onChange={(e) => {
                                setEntries(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                        >
                            <option value="50">50</option>
                            <option value="100">100</option>
                            <option value="150">150</option>
                            <option value="200">200</option>
                        </select>

                        <form className="navbar-search">
                            <input
                                type="text"
                                className="bg-base h-40-px w-auto"
                                placeholder="Search"
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                            <Icon icon="ion:search-outline" className="icon" />
                        </form>

                    </div>

                    <button
                        className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                        data-bs-toggle="modal"
                        data-bs-target="#adsModal"
                        onClick={openCreate}
                    >
                        <Icon icon="ic:baseline-plus" />
                        Add Ads
                    </button>

                </div>

                {/* TABLE */}
                <div className="card-body p-12">
                    <div className="table-responsive">

                        <table className="table bordered-table sm-table">

                            <thead>
                                <tr>
                                    <th>S.L</th>
                                    <th>Image</th>
                                    <th>Title</th>
                                    <th>Description</th>
                                    <th>Link</th>
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {ads.length > 0 ? (
                                    ads.map((a, index) => (
                                        <tr key={a.id}>
                                            <td>{(currentPage - 1) * entries + index + 1}</td>

                                            <td>
                                                <img
                                                    src={getImageUrlHelper(a.path)}
                                                    className="w-40-px h-40-px object-fit-cover radius-8"
                                                />
                                            </td>

                                            <td>{truncateText(a.title, 30)}</td>
                                            <td>{truncateText(a.description, 40)}</td>
                                            <td>{truncateText(a.link || "-", 30)}</td>

                                            <td className="text-center">
                                                <div className="d-flex gap-10 justify-content-center">

                                                    <button
                                                        className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                        data-bs-toggle="modal"
                                                        data-bs-target="#adsModal"
                                                        onClick={() => openEdit(a)}
                                                    >
                                                        <Icon icon="lucide:edit" />
                                                    </button>

                                                    <button
                                                        className="bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                        data-bs-toggle="modal"
                                                        data-bs-target="#deleteModal"
                                                        onClick={() => setDeleteId(a.id)}
                                                    >
                                                        <Icon icon="fluent:delete-24-regular" />
                                                    </button>

                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center">
                                            No ads found
                                        </td>
                                    </tr>
                                )}
                            </tbody>

                        </table>

                    </div>

                    {/* PAGINATION */}
                    <PaginationHelper
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        totalItems={total}
                        pageSize={entries}
                    />

                </div>
            </div>

            <div className="modal fade" id="adsModal">
                <div className="modal-dialog modal-lg modal-dialog-centered">
                    <div className="modal-content radius-16">

                        <div className="modal-header">
                            <h5>{isEdit ? "Update Ads" : "Add Ads"}</h5>
                            <button className="btn-close" data-bs-dismiss="modal" />
                        </div>

                        <div className="modal-body">

                            <div className="row">

                                <div className="col-md-6 mb-3">
                                    <LocationSelectComponent
                                        label="Location"
                                        required
                                        value={formData.location}
                                        onChange={(val) =>
                                            setFormData({ ...formData, location: val })
                                        }
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label>Title <span className="text-danger">*</span></label>
                                    <input
                                        className="form-control"
                                        value={formData.title}
                                        onChange={(e) =>
                                            setFormData({ ...formData, title: e.target.value })
                                        }
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label>
                                        Description <small className="fst-italic">(Max 255 chars)</small>
                                    </label>
                                    <textarea
                                        className="form-control"
                                        rows={5}
                                        maxLength={255}
                                        value={formData.description}
                                        onChange={(e) =>
                                            setFormData({ ...formData, description: e.target.value })
                                        }
                                    />
                                </div>

                                <div className="col-md-6 mb-3">

                                    <label>
                                        Upload Image <span className="text-danger">*</span>
                                    </label>

                                    <label
                                        htmlFor="adsUpload"
                                        className="border radius-8 border-dashed d-flex align-items-center justify-content-center"
                                        style={{ height: "150px", cursor: "pointer" }}
                                    >
                                        {imagePreview ? (
                                            <img
                                                src={imagePreview}
                                                className="w-100 h-100 object-fit-cover radius-8"
                                            />
                                        ) : (
                                            <div className="text-center">
                                                <Icon icon="solar:camera-outline" />
                                                <p><small>(Only image allowed)</small></p>
                                            </div>
                                        )}
                                    </label>

                                    <input
                                        id="adsUpload"
                                        type="file"
                                        hidden
                                        accept=".jpg,.jpeg,.png,.webp"
                                        onChange={handleFileChange}
                                    />

                                </div>
                                <div className="col-md-6 mb-3">
                                    <label>
                                        Link <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        className="form-control"
                                        placeholder="https://example.com"
                                        value={formData.link}
                                        onChange={(e) =>
                                            setFormData({ ...formData, link: e.target.value })
                                        }
                                    />
                                </div>

                            </div>

                            <div className="text-end">
                                <button className="btn btn-primary" onClick={handleSubmit}>
                                    {isEdit ? "Update" : "Create"}
                                </button>
                            </div>

                        </div>

                    </div>
                </div>
            </div>

            {/* DELETE */}
            <div className="modal fade" id="deleteModal">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content radius-16">

                        <div className="modal-header">
                            <h5>Delete Ads</h5>
                            <button className="btn-close" data-bs-dismiss="modal" />
                        </div>

                        <div className="modal-body text-center">
                            <p>Are you sure?</p>

                            <button
                                className="btn btn-danger"
                                onClick={() => {
                                    dispatch(deleteAds(deleteId))
                                        .unwrap()
                                        .then((res) => {
                                            toast.success(res?.message || "Deleted successfully");

                                            dispatch(getAds({
                                                page: currentPage,
                                                limit: entries,
                                                search: debouncedSearch,
                                                locationId: selectedLocation?.value || "",
                                            }));

                                            document.querySelector("#deleteModal .btn-close")?.click();
                                        })
                                        .catch((err) => {
                                            toast.error(
                                                err?.response?.data?.message ||
                                                err?.message ||
                                                "Delete failed"
                                            );
                                        });
                                }}
                            >
                                Delete
                            </button>

                        </div>

                    </div>
                </div>
            </div>
        </>
    );
};

export default AdsLayer;